import {IssueCreatePage} from '@github-ui/issue-create/IssueCreatePage'
import type {IssueCreateDiscussionData, IssueCreateMetadataTypes} from '@github-ui/issue-create/TemplateArgs'
import {CachePreheatContextProvider} from '@github-ui/preheat-issues-manager/cache-preheat-context'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'

import type {AppPayload} from '../app-payload'
import {PotentialDuplicateSidePanel} from '../side-panel/PotentialDuplicateSidePanel'
import type {RepositoryResult} from './internal-issue-new-page-url-arguments-metadata-query-types'

export type IssueNewContentRepository = Pick<
  RepositoryResult,
  | 'id'
  | 'databaseId'
  | 'name'
  | 'nameWithOwner'
  | 'owner'
  | 'isLocked'
  | 'isPrivate'
  | 'isOwnerEnterpriseManaged'
  | 'visibility'
  | 'isArchived'
  | 'isInOrganization'
  | 'hasIssuesEnabled'
  | 'hasAnyTemplates'
  | 'slashCommandsEnabled'
  | 'viewerCanPush'
  | 'isBlankIssuesEnabled'
  | 'viewerCanCreateIssues'
  | 'viewerInteractionLimitReasonHTML'
  | 'viewerIssueCreationPermissions'
  | 'shortDescriptionHTML'
  | 'issueTemplate'
  | 'issueForm'
  | 'suggestedActors'
  | 'labels'
  | 'discussion'
  | 'milestoneByTitle'
  | 'issueType'
>

/**
 * Renders the new-issue form from the resolved URL-argument metadata.
 *
 * Shared by issue creation surfaces that use the Data Router repository payload.
 */
type IssueNewContentProps = {
  repository: IssueNewContentRepository
  duplicateDetectionEnabled?: boolean
  /** Supplied by the data-router route, which has no app payload to read from. */
  currentUserSettings?: {
    preferred_emoji_skin_tone?: number | null
    copilot_show_functionality?: boolean
  }
}

export function IssueNewContent(props: IssueNewContentProps) {
  // Boundary wraps the inner component so a throw in IssueNewContentInner's own render phase
  // (hooks, metadata derivation) is still attributed to IssueNewPage, not the outer generic boundary.
  return (
    <ErrorBoundary boundaryName="IssueNewPage" critical>
      <IssueNewContentInner {...props} />
    </ErrorBoundary>
  )
}

function IssueNewContentInner({repository, currentUserSettings, duplicateDetectionEnabled}: IssueNewContentProps) {
  const appPayload = useAppPayload<AppPayload>()

  const assigneeNodes = repository.suggestedActors?.nodes
  const assignees =
    assigneeNodes && repository.viewerIssueCreationPermissions?.assignable
      ? assigneeNodes.flatMap(e => (e ? [e] : []))
      : undefined

  const labelNodes = repository.labels?.nodes
  const labels =
    labelNodes && repository.viewerIssueCreationPermissions?.labelable
      ? labelNodes.flatMap(e => (e ? [e] : []))
      : undefined

  const projectNodes = repository.owner?.projectsV2ByNumber?.nodes
  const projects =
    projectNodes && repository.viewerIssueCreationPermissions?.triageable ? projectNodes.filter(e => !!e) : undefined

  const milestone =
    repository.milestoneByTitle && repository.viewerIssueCreationPermissions?.milestoneable
      ? repository.milestoneByTitle
      : undefined

  const issueType =
    repository.issueType &&
    repository.viewerIssueCreationPermissions?.triageable &&
    repository.viewerIssueCreationPermissions?.typeable
      ? repository.issueType
      : undefined

  const discussion = repository.discussion
    ? ({
        title: repository.discussion.title,
        formattedBody: repository.discussion.formattedBody,
        labels: repository.discussion.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])),
      } as IssueCreateDiscussionData)
    : undefined

  const initialMetadataValues: IssueCreateMetadataTypes = {
    ...(assignees !== undefined && {assignees}),
    ...(labels !== undefined && {labels}),
    ...(milestone !== undefined && {milestone}),
    ...(projects !== undefined && {projects}),
    ...(issueType !== undefined && {type: issueType}),
    ...(discussion !== undefined && {discussion}),
  }
  const storageKeyPrefix = repository.id || 'hyperlist'
  const userSettings = currentUserSettings ?? appPayload?.current_user_settings
  const emojiSkinTonePreference = userSettings?.preferred_emoji_skin_tone ?? undefined
  const copilotShowFunctionality = userSettings?.copilot_show_functionality || false

  // Same repo the app payload's scoped_repository carried, but available on both transports.
  const ownerLogin = repository.owner?.login
  const duplicateDetectionRepository = ownerLogin ? {owner: ownerLogin, name: repository.name} : undefined

  return (
    <CachePreheatContextProvider>
      <PotentialDuplicateSidePanel repository={duplicateDetectionRepository}>
        {({onPotentialDuplicateClick, onIssuesToPreheat}) => (
          <IssueCreatePage
            initialMetadataValues={initialMetadataValues}
            storageKeyPrefix={storageKeyPrefix}
            emojiSkinTonePreference={emojiSkinTonePreference}
            copilotShowFunctionality={copilotShowFunctionality}
            currentRepository={repository}
            duplicateDetectionEnabled={duplicateDetectionEnabled}
            onPotentialDuplicateClick={onPotentialDuplicateClick}
            onPotentialDuplicatesToPreheat={onIssuesToPreheat}
          />
        )}
      </PotentialDuplicateSidePanel>
    </CachePreheatContextProvider>
  )
}
